<template>
    <section
        v-if="!(isDisplayMobile && slice.primary.hideInMobile)"
        :aria-label="asText(slice.primary.title)"
        :class="['slice persona-slice', {'persona-slice--hideInMobile': slice.primary.hideInMobile}]"
    >
        <div class="slice-content">
            <PrismicRichText :field="slice.primary.title" class="title" />
            <div class="persona persona--mobile">
                <NuxtErrorBoundary>
                    <Swiper :modules="modules" :slides-per-view="1" :pagination="{clickable: true}">
                        <SwiperSlide v-for="(persona, personaIndex) in slice.items" :key="`slice-item-${personaIndex}`">
                            <div class="persona-button">
                                <div class="persona-name-content">
                                    <PrismicImage
                                        v-if="persona.icon && persona.icon.url"
                                        :key="`persona-icon-${personaIndex}`"
                                        :field="persona.icon"
                                        class="icon"
                                        width="28"
                                        height="28"
                                        sizes="small:28px"
                                    />
                                    <div>
                                        {{ persona.persona }}
                                    </div>
                                </div>
                                <div class="persona-rectangle" />
                            </div>
                            <div class="benefits">
                                <div
                                    v-for="benefit in [
                                        {
                                            title: persona.benefitTitle1,
                                            description: persona.benefit1,
                                            index: 1,
                                        },
                                        {
                                            title: persona.benefitTitle2,
                                            description: persona.benefit2,
                                            index: 2,
                                        },
                                        {
                                            title: persona.benefitTitle3,
                                            description: persona.benefit3,
                                            index: 3,
                                        },
                                        {
                                            title: persona.benefitTitle4,
                                            description: persona.benefit4,
                                            index: 4,
                                        },
                                    ]"
                                    :key="`benefit-${benefit.index}`"
                                    class="benefit"
                                >
                                    <PrismicRichText :field="benefit.title" class="benefit-title" />
                                    <PrismicRichText
                                        :field="benefit.description"
                                        :class="{
                                            ellipsed: ellipsedBenefits[personaIndex]?.[benefit.index - 1],
                                        }"
                                    />
                                    <button
                                        v-if="slice.primary.displayFullBenefitText && slice.primary.collapseBenefitText"
                                        class="display-benefit-description"
                                        @click="setEllipsedBenefit(personaIndex, benefit.index - 1)"
                                    >
                                        <div v-if="hasText(benefit.description[0])">
                                            <div v-if="ellipsedBenefits[personaIndex]?.[benefit.index - 1]">
                                                {{ slice.primary.displayFullBenefitText }}
                                            </div>
                                            <div v-else>{{ slice.primary.collapseBenefitText }}</div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </SwiperSlide>
                    </Swiper>
                </NuxtErrorBoundary>
            </div>

            <div class="persona persona--desktop">
                <div class="persona-names" role="tablist">
                    <button
                        v-for="(persona, i) in slice.items"
                        :key="`persona-name-${i}`"
                        :data-testid="`persona-button-${persona.persona}`"
                        :class="{
                            'persona-button': true,
                            selected: isSelectedPersona(i),
                        }"
                        role="tab"
                        :aria-selected="isSelectedPersona(i)"
                        @click="setSelectedPersona(i)"
                    >
                        <div class="persona-name-content">
                            <PrismicImage
                                v-if="persona.icon && persona.icon.url"
                                :field="persona.icon"
                                class="icon"
                                width="28"
                                height="28"
                                sizes="small:28px"
                            />
                            <div :class="{selected: isSelectedPersona(i)}">
                                {{ persona.persona }}
                            </div>
                        </div>
                        <div
                            :class="{
                                'persona-rectangle': isSelectedPersona(i),
                                border: !isSelectedPersona(i),
                            }"
                        ></div>
                    </button>
                </div>
                <div class="benefits">
                    <div
                        v-for="benefit in [
                            {
                                title: slice.items[selectedPersona]?.benefitTitle1,
                                description: slice.items[selectedPersona]?.benefit1,
                                index: 1,
                            },
                            {
                                title: slice.items[selectedPersona]?.benefitTitle2,
                                description: slice.items[selectedPersona]?.benefit2,
                                index: 2,
                            },
                            {
                                title: slice.items[selectedPersona]?.benefitTitle3,
                                description: slice.items[selectedPersona]?.benefit3,
                                index: 3,
                            },
                            {
                                title: slice.items[selectedPersona]?.benefitTitle4,
                                description: slice.items[selectedPersona]?.benefit4,
                                index: 4,
                            },
                        ]"
                        :key="`benefit-${benefit.index}`"
                        :class="{benefit: true, hidden: !isTitleFilled(benefit.title)}"
                    >
                        <PrismicRichText :field="benefit.title" class="benefit-title" />
                        <PrismicRichText :field="benefit.description" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    // @ts-expect-error - Swiper has no types
    import {Pagination, Lazy} from 'swiper';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import type {Ref} from 'vue';
    import {ref, onMounted} from 'vue';
    import {asText} from '@prismicio/helpers';
    import type {RichTextField, TitleField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText, PrismicImage} from '@prismicio/vue';
    import 'swiper/css';
    import 'swiper/scss/pagination';
    import type {PersonaContentSliceSlice} from '@prismicio-types';
    import {usePrismicTypeGuards, usePrismicFieldHelpers} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import useErrorHelpers from '../../composables/useErrorHelpers';

    const props = defineProps(getSliceComponentProps<PersonaContentSliceSlice>());

    const {isDisplayMobile} = useDisplayMobile('Sm');
    const {hasText} = usePrismicFieldHelpers();
    const {hasProperty, isFilled} = usePrismicTypeGuards();

    const modules = [Pagination, Lazy];
    const selectedPersona = ref(0);
    const ellipsedBenefits: Ref<boolean[][]> = ref([
        Array(4).fill(true),
        Array(4).fill(true),
        Array(4).fill(true),
        Array(4).fill(true),
        Array(4).fill(true),
    ]);

    onMounted(() => {
        const {getPreviewErrorHandler, throwIfItemsCountIsOutOfBound, throwIfMissingItemsProperties} = useErrorHelpers();
        const errorHandler = getPreviewErrorHandler();
        if (errorHandler.enabled) {
            const itemsCount = props.slice.items.length;
            throwIfItemsCountIsOutOfBound(errorHandler.toast, itemsCount, 'persona', 2, 5);
            throwIfMissingItemsProperties(
                errorHandler.toast,
                props.slice.items,
                [
                    {name: 'persona', type: 'field'},
                    {name: 'benefitTitle1', type: 'field'},
                    {name: 'benefit1', type: 'field'},
                    {name: 'benefitTitle2', type: 'field'},
                    {name: 'benefit2', type: 'field'},
                    {name: 'icon', type: 'image'},
                ],
                'persona',
            );
        }
    });
    function setSelectedPersona(personaIndex: number) {
        selectedPersona.value = personaIndex;
    }

    function isSelectedPersona(personaIndex: number) {
        return selectedPersona.value === personaIndex;
    }
    function isTitleFilled(title: TitleField | RichTextField | undefined) {
        if (!title) {
            return false;
        }
        const element = title[0];
        if (!hasProperty(element, 'text')) {
            return false;
        }
        return isFilled(element.text);
    }
    function setEllipsedBenefit(personaIndex: number, benefitIndex: number) {
        const newEllipsedBenefits = ellipsedBenefits.value;
        const personaArray = newEllipsedBenefits[personaIndex];
        if (personaArray) {
            personaArray[benefitIndex] = !personaArray[benefitIndex];
            ellipsedBenefits.value = [...newEllipsedBenefits];
        }
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    $margin-section-mobile: 26px;

    @mixin link {
        @include text.getFontBySize(primary-400);
        color: var(--joy-color-secondary-30);
        font-weight: var(--joy-font-weight-bold);
        padding-bottom: 6px;
        border-bottom: 2px solid var(--joy-color-secondary-30);
        text-decoration: none;

        &:hover {
            color: var(--joy-color-secondary-50);
            transition: color var(--joy-transition-duration-default) var(--joy-transition-timing-function);
        }
    }

    .persona-slice {
        font-family: var(--joy-font-family-base);
        color: var(--joy-color-neutral-70);
        border-bottom-right-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;

        &--hideInMobile {
            display: none;
        }

        @include mq.screen_sm(min) {
            align-items: center;
            display: block;
        }

        .title h2 {
            @include text.getFontBySize(primary-800);
            font-weight: var(--joy-font-weight-bold);
            text-align: left;
            margin-bottom: var(--joy-core-spacing-8);

            @include mq.screen_sm(min) {
                @include text.getFontBySize(secondary-600);
                text-align: center;
                margin-bottom: 48px;
            }
        }

        .swiper {
            overflow: hidden;
            position: relative;
            z-index: 0;
            max-width: calc(100vw - 2 * #{$margin-section-mobile});
            margin-bottom: 48px;
            .swiper-slide {
                width: 100%;
            }
            .swiper-slide .persona-button {
                color: var(--joy-color-neutral-30);

                .icon {
                    filter: grayscale(100%) brightness(2.5);
                    margin-right: var(--joy-core-spacing-5);
                    width: 28px;
                    height: 28px;
                }
                margin-bottom: 35px;
            }

            .swiper-slide-active .persona-button {
                .persona-name-content {
                    color: var(--joy-color-secondary-30);
                }

                .icon {
                    filter: unset;
                    margin-right: var(--joy-core-spacing-5);
                    width: 28px;
                    height: 28px;
                }
            }

            .swiper-pagination {
                position: relative;
                z-index: 0;
                margin-top: var(--joy-core-spacing-4);

                &.swiper-pagination-bullets {
                    display: flex;
                    justify-content: center;
                    gap: var(--joy-core-spacing-2);

                    .swiper-pagination-bullet {
                        background-color: var(--joy-color-neutral-30);
                        opacity: 1;
                    }

                    .swiper-pagination-bullet-active {
                        background-color: var(--joy-color-secondary-30);
                    }
                }
            }
        }

        .persona-names {
            display: flex;
            width: 100%;
        }

        .persona-rectangle {
            background: var(--joy-color-secondary-30);
            border-radius: var(--joy-core-radius-3);
            height: 4px;
            transition: all 0.2s ease;
            width: 100%;
        }

        .persona-button {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-end;
            padding: 0px;
            background-color: transparent;
            color: var(--joy-color-neutral-30);
            font-weight: var(--joy-font-weight-bold);
            border: 0px;
            font-size: var(--joy-font-size-primary-600);
            line-height: var(--joy-line-height-large);
            width: 100%;

            .icon {
                filter: grayscale(100%) brightness(2.5);
                margin-right: var(--joy-core-spacing-5);
                width: 28px;
                height: 28px;
            }

            &.selected {
                color: var(--joy-color-secondary-30);

                .icon {
                    filter: unset;
                }
            }

            .persona-name-content {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                margin: 10px var(--joy-core-spacing-5);
            }

            .border {
                background: var(--joy-color-neutral-30);
                border-radius: var(--joy-core-radius-3);
                height: 2px;
                transition: all 0.2s ease;
                width: 100%;
            }
        }

        .persona {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 40px;
            line-height: 150%;
            text-align: left;

            &--mobile {
                display: block;

                @include mq.screen_sm(min) {
                    display: none;
                }
            }

            &--desktop {
                display: none;

                @include mq.screen_sm(min) {
                    display: flex;
                }
            }
        }

        .benefit-title h3 {
            @include text.getFontBySize(primary-600);
            font-weight: var(--joy-font-weight-bold);
            line-height: 140%;
            color: var(--joy-color-text-default);
            margin-bottom: 0;

            @include mq.screen_sm(min) {
                margin-bottom: var(--joy-core-spacing-5);
            }
        }

        .benefits {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-9);
            padding-bottom: var(--joy-core-spacing-6);
            width: 100%;

            @include mq.screen_sm(min) {
                flex-direction: row;
                gap: 64px;
                padding-bottom: 0;
            }
        }

        .link {
            @include link;
        }

        .benefit {
            flex: 1 1 0;
            color: var(--joy-color-text-default);
            line-height: var(--joy-line-height-large);
            font-size: var(--joy-font-size-primary-400);
            margin-bottom: 10px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;

            p {
                overflow: visible;
            }

            .ellipsed {
                p {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
            }

            @include mq.screen_sm(min) {
                margin-bottom: 0;
                display: block;
                gap: 0;
            }
        }

        .hidden {
            display: none;
        }

        .display-benefit-description {
            border: none;
            padding: 0px;
            @include link;
        }
    }
</style>
